// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { RotateCw } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'

// The "Reset demo data" control that lived inside the old AuditTab (SaaS /admin/audit only).
// Moved unchanged when AuditTab was replaced by AuditExplorer (D-AUDIT-2); the audit log itself
// is preserved by the reset (ADR-006, append-only).
export default function DemoResetControl() {
  const [open, setOpen] = useState(false)
  const reset = useMutation({ mutationFn: api.demoReset, onSuccess: () => setOpen(false) })

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} style={resetBtn}>
        <RotateCw size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
        Reset demo data
      </button>
      {reset.data && <span role="status" style={{ fontSize: 12, color: '#065f46' }}>✓ {reset.data.message}</span>}
      {reset.isError && <span role="alert" style={{ fontSize: 12, color: '#dc2626' }}>{friendlyError(reset.error, 'Reset failed')}</span>}
      {open && (
        <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setOpen(false) }}>
          <div style={dialogStyle}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 600 }}>Reset demo data?</h2>
            <p style={{ fontSize: 13, color: 'var(--color-text-soft)', marginTop: 0 }}>This will:</p>
            <ul style={{ fontSize: 13, color: 'var(--color-text)', marginTop: 4, paddingLeft: 20 }}>
              <li>Delete all prescriptions created during demo sessions</li>
              <li>Delete all fills, refill requests, and state log entries linked to demo Rx</li>
              <li>Restore seeded prescriptions to their original starting states</li>
              <li>Preserve the audit log (per ADR-006, append-only)</li>
            </ul>
            <p style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 12 }}>
              Server-name guard ensures this only runs against the mockup database.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button type="button" onClick={() => setOpen(false)} disabled={reset.isPending}
                style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 13, color: 'var(--color-text-soft)' }}>
                Cancel
              </button>
              <button type="button" onClick={() => reset.mutate()} disabled={reset.isPending} style={resetBtn}>
                {reset.isPending ? 'Resetting…' : 'Yes, reset demo data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const resetBtn: React.CSSProperties = { padding: '8px 14px', background: 'transparent', border: '1px solid #dc2626', color: '#dc2626', borderRadius: 4, fontSize: 13, fontWeight: 500 }
const overlayStyle: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(28,25,23,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 200, padding: 16 }
const dialogStyle: React.CSSProperties = { background: 'var(--color-surface)', borderRadius: 8, padding: 20, width: '100%', maxWidth: 480, boxShadow: 'var(--shadow-lg)' }
